/**
 * useEmbeddedChannel — bootstrap do canal host ↔ Efflux (SÓ no alvo embedded).
 * ===========================================================================
 *
 * GATED por `platform === 'embedded'` → em todos os outros alvos (tizen/webos/
 * web/android/browser) é **no-op absoluto**: o efeito retorna cedo e nada é
 * instalado. Só ativa dentro do WebView do AAR (onde `__effluxEmbedded` está
 * setado e o detectPlatform resolve 'embedded').
 *
 * Liga os handlers reais (streamStore/volumeService/platform/window) ao
 * {@link embeddedHostChannel}, sinaliza `ready`+`started` pro host, emite
 * `contentChanged` quando o `libraryHash` do sync muda, e bate um `heartbeat`
 * periódico (Fase 9 — self-healing: o AAR usa como watchdog anti-freeze).
 */
import { useEffect } from 'react';
import { reaction } from 'mobx';

import { usePlatform } from '~/services/platform';
import { useStore } from '~/stores';
import { VolumeService } from '~/services/volume/VolumeService';
import { readStoredServerVolume } from '~/services/config/playerConfigStorage';
import { captureScreenBase64 } from '~/utils/captureScreen';
import {
  embeddedHostChannel,
  createStoreCommandHandlers,
  isHostVolumeControlled,
  getHostVolumeLevel,
} from '~/services/embedded';

/**
 * Intervalo do heartbeat pro host (Fase 9). ~5s é curto o bastante pra o watchdog
 * do AAR detectar freeze rápido (tolerância ~20s = 4 batimentos perdidos) e leve o
 * bastante pra não pesar (uma chamada de bridge a cada 5s).
 */
const HEARTBEAT_INTERVAL_MS = 5_000;

export function useEmbeddedChannel(): void {
  const platform = usePlatform();
  const { streamStore, syncStore, authStore } = useStore();

  useEffect(() => {
    // Gate absoluto: nada acontece fora do embedded.
    if (platform.platform !== 'embedded') return;

    const volumeService = VolumeService.getInstance();
    const handlers = createStoreCommandHandlers({
      setHostHidden: (hidden) => streamStore.setHostHidden(hidden),
      setVolume: (level) => volumeService.setVolume(level),
      onHide: () => platform.onHide(),
      onResume: () => platform.onResume(),
      reload: () => window.location.reload(),
      setHostTags: (tags) => syncStore.setHostTags(tags),
      // Host-owned volume: no releaseVolume, o handler reaplica o volume da última
      // config do servidor (persistida pelo ConfigManager) → controle volta ao Efflux.
      getServerVolume: () => readStoredServerVolume(),
      // Avisa o host o volume efetivo + quem controla (evento `volumeChanged`).
      emitVolumeChanged: (level, source) =>
        embeddedHostChannel.emit('volumeChanged', { level, source }),
      // Path 3 — captura o frame atual (DOM+vídeo, MESMA via do screenshot do
      // admin/RemoteOps: `captureScreenBase64`) e devolve pro host via a bridge
      // nativa `window.EffluxHost.onCoverFrame`. O host usa como cover enquanto
      // recria a WebView (reset do address space do renderer 32-bit) → sem preto.
      // Best-effort: qualquer falha entrega '' e o host recria sem cover.
      captureCoverFrame: async () => {
        let base64 = '';
        try {
          const cap = await captureScreenBase64();
          base64 = cap?.imageBase64 ?? '';
        } catch {
          base64 = '';
        }
        try {
          window.EffluxHost?.onCoverFrame?.(base64);
        } catch {
          /* bridge ausente/AAR antigo — player segue normal */
        }
      },
    });

    embeddedHostChannel.init(handlers);
    embeddedHostChannel.markPlayerReady({
      externalId: authStore.deviceId || undefined,
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      platform: 'embedded',
    });
    embeddedHostChannel.emit('started');

    // Estado inicial de volume pro host (host-owned volume): se o host já detinha
    // o controle (sticky, persistido), reporta o nível dele; senão reporta o volume
    // configurado no servidor que o player recebeu. O host exibe o valor correto no
    // boot sem precisar mexer no volume.
    {
      const hostControlled = isHostVolumeControlled();
      const initialLevel = hostControlled
        ? getHostVolumeLevel()
        : readStoredServerVolume();
      if (typeof initialLevel === 'number') {
        embeddedHostChannel.emit('volumeChanged', {
          level: initialLevel,
          source: hostControlled ? 'host' : 'server',
        });
      }
    }

    // Avisa o host quando a biblioteca muda (novo libraryHash pós-sync).
    const disposeContentChanged = reaction(
      () => syncStore.libraryHash,
      (hash) => {
        if (hash)
          embeddedHostChannel.emit('contentChanged', { libraryHash: hash });
      },
    );

    // Fase 9 — heartbeat periódico pro watchdog do AAR, DIRETO na bridge (fora do
    // buffer de eventos: batimento é fire-and-forget). Imediato + a cada intervalo.
    // Enquanto o JS estiver rodando (event loop vivo) o batimento sai; se a WebView
    // congelar, os batimentos param → o AAR recupera (Fase 9b/9c).
    embeddedHostChannel.emitHeartbeat(Date.now());
    const heartbeatTimer = setInterval(() => {
      embeddedHostChannel.emitHeartbeat(Date.now());
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      disposeContentChanged();
      clearInterval(heartbeatTimer);
    };
    // Só roda uma vez por montagem do app embedded (platform é estável).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform.platform]);
}
